import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import { getInstance } from '@maple-tech/auth/Auth';
import { UserModel } from '@maple-tech/auth/UserModel';
import { authService } from './MapleService';
import { useUserController } from '@maple-tech/auth/userState';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';

const APP_TESTER_EMAIL = 'app-tester@workmaple.com';

function LoginRequestBox() {
  const [email, setEmail] = useState('');
  const [acceptionCode, setAcceptionCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [error, setError] = useState(null);
  const userState = useUserController();
  const [requestTime, setRequestTime] = useState<Dayjs>(null);
  const [password, setPassword] = useState('');
  const handleEmailChange = (event) => {
    setEmail(event.target.value.trim().toLowerCase());
    setError(null); // Her değişiklikte hatayı temizle
  };
  const handlePasswordChange = (event) => {
    if (email === APP_TESTER_EMAIL) {
      setPassword(event.target.value);
      setError(null); // Her değişiklikte hatayı temizle
    }
  };
  const { t } = useTranslation();
  const reset = () => {
    setRejected(false);
    setRequestSent(false);
    setAcceptionCode('');
    setEmail('');
    setRequestTime(null);
  };
  // TODO: Bu geçici olarak aktif olan bir fonksiyondur.
  // TODO: Appstore onaylama sürecinde testerların erişmesi için kullanılacaktır ve ileride kaldırılacaktır.
  const appStoreTesterLogin = async (contact: string, password: string) => {
    try {
      const response = await getInstance('CoreAPI').post('/login', {
        contact,
        password,
      });
      if (response.status === 200) {
        // Başarılı yanıt
        console.log('App Store Tester Login başarılı:', response.data);
        authService.setAuthKey(response.data.authentication);
        // @ts-ignore
        const userbase = response.data.user as UserModel;
        // @ts-ignore
        authService.setXCompanyID(userbase.companies[0].id);
        const { data: employee, status } = await getInstance('RetailAPI').get(
          '/employee/' + userbase.id,
        );
        if (status === 200) {
          userState.setUser({
            ...userbase,
            ...employee,
          });
          await userState.refresh();
          // window.location.href = '/'; // Yönlendirme
        } else {
          console.error('Employee bilgisi alınamadı:', status);
        }
      }
    } catch (error) {
      console.error('App Store Tester Login hatası:', error);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setLoading(true);
    setRequestSent(false);
    setError(null);
    setRequestTime(dayjs());
    if (email === APP_TESTER_EMAIL && password) {
      // App Store Tester için giriş yapma işlemi
      await appStoreTesterLogin(email, password);
      setLoading(false);
      return;
    }
    const formData = new FormData();
    formData.append('manager_mail', email);
    // Burada gerçek bir API çağrısı yapmalısınız
    // Örnek olarak, 2 saniyelik bir gecikme ekliyoruz
    try {
      const response = await getInstance('AuthAPI').post(
        '/validator/animator/store-manager',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      if (response.status !== 200) {
        setRequestSent(false);
        return;
      }
      if (response.data?.message === 'rejected') {
        setRejected(true);
        return;
      }
      if (!!response.data?.code) {
        setRequestSent(true);
        setAcceptionCode(response.data.code);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const onConfirm = async ({
    user,
    authentication,
  }: {
    user: UserModel;
    authentication: string;
  }) => {
    try {
      reset();
      authService.setAuthKey(authentication);
      // @ts-ignore
      authService.setXCompanyID(user.companies[0].id);
      localStorage.setItem(
        'sessionControlToken',
        Number(new Date()).toString(),
      );
      userState.setUser(user);
      await userState.refresh();
      // window.location.href = '/'; // Yönlendirme
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Container
      maxWidth="xs"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <Paper
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: 300,
          p: 5,
        }}
      >
        <Typography component="h1" variant="h5">
          {/* Login Request */}
          {t('App.LoginRequestBox.title')}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          {rejected ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {/* Your request has been rejected. Please try again later. */}
              {t('App.LoginRequestBox.rejectedMessage')}
              <br />
              <br />
              <Button
                variant="contained"
                onClick={() => {
                  reset();
                }}
              >
                {/* Try again */}
                {t('App.LoginRequestBox.tryAgainButton')}
              </Button>
            </Alert>
          ) : !requestSent ? (
            <>
              <Typography
                component="p"
                variant="body2"
                sx={{ mt: 2, textAlign: 'center' }}
              >
                {/* Send a request to your store manager to log in. You can enter
                the store email address in the input field below */}
                {t('App.LoginRequestBox.instructions')}
              </Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label={t('App.LoginRequestBox.emailLabel')} //"Store Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={handleEmailChange}
                disabled={loading}
                error={!!error} // Boolean'a çevir
              />
              {email === APP_TESTER_EMAIL && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="password"
                  label={'Password'}
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={handlePasswordChange}
                  disabled={loading}
                />
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {/* Send Entry Request */}
                {t('App.LoginRequestBox.submitButton')}
              </Button>
            </>
          ) : (
            <>
              <Typography
                component="p"
                variant="body2"
                sx={{ mt: 2, textAlign: 'center' }}
              >
                {/* Your request has been sent to the store manager. You will be
                notified when the approval process is completed. */}
                {t('App.LoginRequestBox.successMessage')}
              </Typography>
              <Stack
                spacing={2}
                mt={4}
                justifyContent={'center'}
                flexDirection="row"
                alignItems="center"
              >
                <CircularProgress size={24} />
                <OneMinuteCounter
                  requestTime={requestTime}
                  managerMail={email}
                  acceptionCode={acceptionCode}
                  onTimeout={() => {
                    setRejected(true);
                  }}
                  onConfirm={onConfirm}
                />
                <Typography
                  component="p"
                  variant="body2"
                  sx={{ textAlign: 'center' }}
                >
                  {/* Waiting for approval */}
                  {t('App.LoginRequestBox.waitingApproval')}
                </Typography>
              </Stack>
            </>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
        <LanguageSelector mode="list" />
      </Paper>
    </Container>
  );
}

const OneMinuteCounter = ({
  managerMail,
  acceptionCode,
  onTimeout,
  onConfirm,
  requestTime,
}: {
  managerMail: string;
  acceptionCode: string;
  onTimeout?: () => void;
  requestTime: Dayjs;
  onConfirm?: (resp: { user: UserModel; authentication: string }) => void;
}) => {
  const [seconds, setSeconds] = useState(60); // Başlangıç saniyesini burada tanımlayın
  const intervalRef = useRef(null);
  const abortControllerRef = useRef<AbortController>(null);
  const requestInProgressRef = useRef(false);
  const { t } = useTranslation();
  useEffect(() => {
    let timeoutId; // setTimeout için referans

    const checkAcception = async () => {
      // request time is 1 minute
      const diff = dayjs().diff(requestTime, 'second');
      if (diff > 60) {
        onTimeout?.();
        clearTimeout(timeoutId); // setTimeoutu temizle
        return;
      }
      if (requestInProgressRef.current) {
        return;
      }

      requestInProgressRef.current = true;
      abortControllerRef.current = new AbortController();

      try {
        const response = await getInstance('AuthAPI').post(
          '/validator/animator/store-manager/acception-check',
          {
            manager_mail: managerMail,
            code: acceptionCode,
          },
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            signal: abortControllerRef.current.signal,
          },
        );

        if (response.status === 200 && response.data?.user) {
          onConfirm?.(response.data);
          clearTimeout(timeoutId); // setTimeoutu temizle
          setSeconds(-1);
        }
        if (response.status === 500) {
          onTimeout?.();
        }
      } catch (error) {
        console.error('Acception check error:', error);
        if (error.name !== 'AbortError') {
          // Hata yönetimi
        }
      } finally {
        requestInProgressRef.current = false;
      }
    };

    const scheduleNextCheck = () => {
      timeoutId = setTimeout(() => {
        queueMicrotask(() => {
          if (seconds >= 0) {
            const diff = dayjs().diff(requestTime, 'second');
            setSeconds((prev) => 60 - diff);
            checkAcception();
            scheduleNextCheck();
          } else {
            onTimeout?.();
          }
        });
      }, 500); // 1 saniyede kontrol et
    };

    scheduleNextCheck(); // İlk kontrolü planla

    return () => {
      clearTimeout(timeoutId); // Temizle
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [managerMail, acceptionCode, onConfirm, onTimeout, requestTime]);
  return (
    <Chip
      label={`${
        seconds >= 0
          ? `${seconds} ${t('App.LoginRequestBox.seconds')}`
          : t('App.LoginRequestBox.timeout')
      }`}
      size="small"
    />
  ); // Timeout durumunu göster
};

export default LoginRequestBox;
